import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { getApplications, getAutoInstalledPlugins } from '@app/common/store/profiles/selectors';
import { useUserPluginRestrictedMode } from '@app/pages/profiles/profile-details/profile-ide-provisioner/hooks/user-restricted-modes';
import { PluginRulesBundleDialog } from '@modules/ide-provisioner/plugins-rules/plugin-rules-bundle-dialog/plugin-rules-bundle-dialog';
import { LegacyFeatureType } from '@modules/ide-provisioner/plugins-rules/plugin-rules-bundle-dialog/legacy-feature-type';
import { autoInstallPluginSettingToBundle, pluginRuleBundleToAutoInstallPluginSetting } from '@modules/ide-provisioner/plugins-rules/common/auto-install-plugins-legacy-converter';
import { usePluginData } from '@modules/plugins/use-plugin-data';
import { ProfileActions } from '@common/store/profiles/profile-reducer';
import { assignTestId } from '@common/infra/tests/assign-test-id';
import { ControlledModal } from '@common/modals';
import { SettingsList } from '../../common/settings-list';
import SettingsListEntry from '../../common/settings-list-entry';
import { SettingPlaceholder } from '../../common/settings-placeholder';
import { useSettingsContext } from '../../context/settings-context';
import { showFilteredSettings, showHiddenSettingsPlaceholder } from '../../functions/settings';
import { filterByFocusedApp, useProfilesParams } from '../../context/profiles-params-context';
function AutoInstalledPlugins(props) {
    var _a, _b;
    const { profileId } = props;
    const autoInstalledPluginSettings = useSelector(getAutoInstalledPlugins);
    const applications = useSelector(getApplications);
    const { focusedApp } = useProfilesParams();
    const filteredPlugins = filterByFocusedApp(autoInstalledPluginSettings, focusedApp);
    const dispatch = useDispatch();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const { setFormState, showForm, resetFormState, settingId } = useSettingsContext('autoInstalled');
    const deleteEntry = (settingId) => {
        dispatch(ProfileActions.deleteSetting({ settingType: 'autoInstalled', settingId }));
    };
    const { isRestricted } = useUserPluginRestrictedMode();
    const submitContentForm = useCallback((values) => {
        dispatch(ProfileActions.updateAutoInstalledPlugins(values));
        setIsCollapsed(false);
        resetFormState();
    }, [resetFormState, dispatch, setIsCollapsed]);
    const handleOnCloseDalogAttempt = useCallback((values) => {
        if (values) {
            submitContentForm(pluginRuleBundleToAutoInstallPluginSetting(values.bundle, values.tools));
        }
        else {
            resetFormState();
        }
    }, [submitContentForm, resetFormState]);
    const autoInstallSetting = useMemo(() => autoInstalledPluginSettings.find(({ id }) => id === settingId), [autoInstalledPluginSettings, settingId]);
    const pluginDataRequired = !!((_a = autoInstallSetting === null || autoInstallSetting === void 0 ? void 0 : autoInstallSetting.content) === null || _a === void 0 ? void 0 : _a.length);
    const { data: pluginsData, isFetching: isPluginsDataFetching } = usePluginData((_b = autoInstallSetting === null || autoInstallSetting === void 0 ? void 0 : autoInstallSetting.content.map(value => value.pluginId)) !== null && _b !== void 0 ? _b : [], pluginDataRequired);
    const pluginRulesBundle = useMemo(() => {
        if (!autoInstallSetting) {
            return undefined;
        }
        return autoInstallPluginSettingToBundle(autoInstallSetting, pluginsData);
    }, [autoInstallSetting, pluginsData]);
    return (_jsxs(_Fragment, { children: [_jsxs(SettingsList, { title: "auto-installed", isCollapsed: isCollapsed, onToggleCollapsed: () => setIsCollapsed(prev => !prev), ...assignTestId('auto-installed-plugins-setting'), children: [isEmpty(autoInstalledPluginSettings) && (_jsx(SettingPlaceholder, { title: "No auto-installed plugins yet" })), showHiddenSettingsPlaceholder(autoInstalledPluginSettings, filteredPlugins) && (_jsx(SettingPlaceholder, { title: autoInstalledPluginSettings.length === 1
                            ? 'One plugin is hidden'
                            : `${autoInstalledPluginSettings.length} plugins are hidden` })), showFilteredSettings(filteredPlugins) &&
                        filteredPlugins.map(entry => (_jsx(SettingsListEntry, { entry: entry, onEditClick: setFormState, onDeleteClick: deleteEntry, settingType: "autoInstalled" }, entry.id)))] }), showForm && !(pluginDataRequired && isPluginsDataFetching) && (_jsx(ControlledModal, { component: PluginRulesBundleDialog, dialogProps: {
                    pluginRulesBundle,
                    profileId,
                    applicationFilters: applications.rules,
                    legacyFeatureType: LegacyFeatureType.AutoInstallPlugins,
                    isRestrictedMode: isRestricted
                }, onCloseAttempt: handleOnCloseDalogAttempt }))] }));
}
export default AutoInstalledPlugins;
